import { setupApi } from '@/api/config-setup'
import { DatabaseAlerIcon } from '@/components/icons'
import useDiscoverDataDecoder from '@/features/design-system/hooks/setup/useDiscoverData'
import { CONFIG_SETUP_ROUTES } from '@/features/design-system/routes'
import { NotifyService } from '@/helpers'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SetUpHeader from '../../../common/SetUpHeader'
import SetUpLayout from '../../../common/SetUpLayout'
import ReviewControllers from './components/ReviewControllers'
import classes from './styles.module.scss'
import useSearchInterface from '../../../../../admin/hooks/interface/useSearchInterface'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useTranslation from '@/hooks/useTranslation'
import useMk2ImportSettling from '@/features/config/hooks/controller/useMk2ImportSettling'

/**
 * SCRUM-1032 — how long the discovery has been running, as a clock.
 *
 * The controller decides how long this takes and the endpoint reports nothing
 * on the way, so there is no percentage to show. What can be shown honestly is
 * the time on the wall: it moves, which says the request is alive, and it is
 * the only number here that means what it says.
 */
const formatElapsed = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const rest = String(seconds % 60).padStart(2, '0')
  return `${minutes}:${rest}`
}

type Props = {}

const DiscoveryData = (props: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  const { id } = useParams()
  const interfaceId = Number(id)

  const [isError, setIsError] = useState<boolean>(false)
  const [isOpenReviewController, setIsOpenReviewController] =
    useState<boolean>(false)
  const [elapsed, setElapsed] = useState(0)

  const { data: allInterfaces } = useSearchInterface()

  // Called for its redirect-guard side effect; nothing here reads its result.
  useValidateCurrentInterface({
    currentInterfaceId: id,
    allInterfaces,
    redirectTo: OPERATIONS_ROUTES.index.to,
    autoRedirect: false,
  })


  const fetchData = useCallback(async () => {
    setIsError(false)
    setElapsed(0)
    startedAtRef.current = Date.now()
    await setupApi
      .getDiscoverData(Number(interfaceId))
      .then(() => {
        // Straight through. The review used to be held back two seconds after
        // the data had arrived, for nothing but the look of the thing.
        setIsOpenReviewController(true)
      })
      .catch((e) => {
        NotifyService.error(e)
        setIsError(true)
      })
  }, [interfaceId])

  // A discovery deletes every decoder the controller does not report back. If
  // an MK2 import into this controller is still landing (or its automatic
  // resync is running), starting one now can take the imported decoders with
  // it — so the discovery this page opens with waits for that to finish.
  const isImportSettling = useMk2ImportSettling(interfaceId)
  const discoveredForRef = useRef<number | null>(null)
  useEffect(() => {
    if (isImportSettling) return
    if (discoveredForRef.current === interfaceId) return
    discoveredForRef.current = interfaceId
    fetchData()
  }, [fetchData, interfaceId, isImportSettling])

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(id || ''))
  }

  const startedAtRef = useRef(Date.now())

  // The clock runs while the request is out and stops when it lands, one way
  // or the other. It replaces a percentage that was invented here rather than
  // reported by the controller: it climbed 10% every five seconds to 90,
  // waited however long the discovery actually took, then jumped to 100 —
  // reading "89%" on a controller that had barely started, and standing at
  // 100% while the screen still said it was working.
  useEffect(() => {
    if (isError || isOpenReviewController) return
    const intervalId = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000))
    }, 1000)
    return () => clearInterval(intervalId)
  }, [isError, isOpenReviewController])

  const { mutate: discoverData, isLoading: isResyncing } =
    useDiscoverDataDecoder()

  const onResync = () => {
    discoverData({
      interfaceId: interfaceId,
    })
  }

  return (
    <SetUpLayout
      title={t('setupStepsDiscoverData')}
      header={<SetUpHeader hideSelect={true} />}
    >
      <Box className={classes.root}>
        <Typography color="text.main" className={classes.pageTitle}>
          {t('setupStepsDiscoverData')}
        </Typography>
        {!isError ? (
          <>
            <Stack className={classes.spinner}>
              <CircularProgress size={56} thickness={3.6} />
            </Stack>
            <Typography color="primary.main" className={classes.title}>
              {isImportSettling && discoveredForRef.current !== interfaceId
                ? t('discoveryDataWaitingForImport')
                : t('discoveryDataReadingController')}
            </Typography>
            <Typography className={classes.description}>
              {t('discoveryDataKeepWindowOpen')}
            </Typography>
            {/* The one number here that means what it says. */}
            <Typography className={classes.elapsed}>
              {t('discoveryDataElapsed', { time: formatElapsed(elapsed) })}
            </Typography>
            <Box className={classes.actions}>
              {/* Secondary: cancelling throws the discovery away, and it was
                  the only control on screen wearing the primary treatment. */}
              <Button
                variant="secondary"
                className={classes.btnCanel}
                onClick={onBack}
              >
                {t('commonActionsCancel')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <DatabaseAlerIcon className={classes.errorIcon} />
            <Typography className={classes.title}>
              {t('discoveryDataErrorTitle')}
            </Typography>
            <Typography className={classes.description}>
              {t('discoveryDataErrorDescription')}
            </Typography>
            <Box className={classes.actions}>
              <Button
                variant="secondary"
                className={classes.btnCanel}
                onClick={onBack}
              >
                {t('commonActionsBack')}
              </Button>
              <Button
                variant="primary"
                className={classes.btnTry}
                onClick={fetchData}
                disabled={isImportSettling}
              >
                {t('tryAgain')}
              </Button>
            </Box>
          </>
        )}
      </Box>
      {isOpenReviewController && (
        <ReviewControllers
          isResyncing={isResyncing}
          open={isOpenReviewController}
          onResync={onResync}
        />
      )}
    </SetUpLayout>
  )
}

export default DiscoveryData
